import { pathnameIncludes } from '@/shared/lib/util/location';
import type { MainMenuItem } from '@/shared/model';
import { MainMenuKeys, useAppFrameStore } from '@/shared/model';
import CollapsibleListGroup from '@/shared/ui/components/CollapsibleListGroup';
import FiltersCollection from '@/shared/ui/components/FiltersCollection';
import ListItemNavLink from '@/shared/ui/components/ListItemNavLink';
import type { ResolvedMenuNode } from '@/shared/ui/components/SideMenus/menuCatalog';
import { resolveMenuTree } from '@/shared/ui/components/SideMenus/menuCatalog';
import useMenuContext from '@/shared/ui/components/SideMenus/useMenuContext';
import { Body1, H5 } from '@/shared/ui/components/Typography';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import { Box, CssBaseline, List, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

type MenuItemsProps = {
  items: MainMenuItem[];
};
// No onClick navigate here: ListItemNavLink is a NavLink, which already navigates on click.
const MenuItems = ({ items }: MenuItemsProps) => (
  <>
    {items.map(item => (
      <ListItemNavLink
        key={item.key ?? item.label}
        to={item.navigatePath}
        icon={item.icon}
        title={item.label}
        listItemButtonProps={item.buttonProps}
      />
    ))}
  </>
);

const PrimaryListHeader = ({ label }: { label: string }) => (
  <H5
    sx={{
      color: 'inherit'
    }}
  >
    {label}
  </H5>
);

type MenuNodeProps = {
  node: ResolvedMenuNode;
  depth: number;
  /** Non-catalog content injected into a node, keyed by section. */
  extras?: Record<string, { before?: ReactNode; after?: ReactNode }>;
};

/**
 * Renders one node of the catalog.  A node with a title is a collapsible group; one without
 * renders its items inline.  Top-level groups get the large header, nested ones the small one.
 */
const MenuNode = ({ node, depth, extras }: MenuNodeProps) => {
  const extra = node.key ? extras?.[node.key] : undefined;

  const body = (
    <>
      {extra?.before}
      <MenuItems items={node.items} />
      {node.children.map((child, index) => (
        <MenuNode key={child.key ?? child.title ?? index} node={child} depth={depth + 1} extras={extras} />
      ))}
      {extra?.after}
    </>
  );

  if (!node.title) {
    return body;
  }

  return (
    <CollapsibleListGroup
      primaryText={depth === 0 ? <PrimaryListHeader label={node.title} /> : node.title}
      icon={node.icon}
      // Every group starts open: the nav is a list to scan, not a puzzle to unfold.
      defaultOpen={true}
      {...(depth === 0 && { my: 2 })}
    >
      {depth === 0 ? (
        body
      ) : (
        <List>
          <Box sx={{ paddingLeft: 2 }}>{body}</Box>
        </List>
      )}
    </CollapsibleListGroup>
  );
};

const MainSideMenu = () => {
  const { mainMenuItems } = useAppFrameStore();
  const menuContext = useMenuContext();
  // ListItemNavLink reads window.location to decide what is selected, so the menu has to render
  // again when the route changes.  Re-rendering keeps the open/closed groups; remounting did not.
  useLocation();

  const tree = useMemo(() => resolveMenuTree(menuContext), [menuContext]);
  // The `?? []` built a new array every render, which defeated the memo below -- now that the
  // menu re-renders on every navigation, that mattered.
  const recentPatients = useMemo(() => mainMenuItems.get(MainMenuKeys.RecentPatients)?.options ?? [], [mainMenuItems]);

  // Two entries in the Patients group are not catalog data: the recent-patient list comes from
  // local history, and the saved-filter collection is its own component.  Both hang off the
  // Patients node rather than turning the catalog into a place that knows about components.
  const extras = useMemo(
    () => ({
      [MainMenuKeys.Patients]: {
        before: recentPatients.length > 0 && (
          <CollapsibleListGroup
            primaryText={
              <Stack
                direction={'row'}
                spacing={1}
                sx={{
                  alignItems: 'center'
                }}
              >
                <RecentActorsIcon fontSize={'small'} />
                <Body1
                  sx={{
                    color: 'inherit'
                  }}
                >
                  Recent Patients
                </Body1>
              </Stack>
            }
            defaultOpen={true}
          >
            <MenuItems items={recentPatients} />
          </CollapsibleListGroup>
        ),
        after: !menuContext.isRpmViewable && !menuContext.isProdEnvironment && (
          <FiltersCollection selected={pathnameIncludes('patients/list')} />
        )
      }
    }),
    [recentPatients, menuContext.isRpmViewable, menuContext.isProdEnvironment]
  );

  return (
    <Stack
      direction="row"
      sx={{
        height: '100%'
      }}
    >
      <CssBaseline />
      <List component="nav" disablePadding={true}>
        {tree.map(node => (
          <MenuNode key={node.key ?? node.title} node={node} depth={0} extras={extras} />
        ))}
      </List>
    </Stack>
  );
};

export default MainSideMenu;
